import * as React from "react";
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    ListItem,
    ListItemButton,
    Divider,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import type { MyTask } from "../myTasksSlice";
import { DocumentAvatar } from "../../documents/controls/DocumentAvatar";
import {
    getStatus,
    urgencyAppearance,
} from "../utils/taskUrgency";
import { TaskDeadline } from "./TaskDeadline";

type TaskListItemProps = {
    task: MyTask;
    now: Date;
    onOpen: (task: MyTask) => void;
    divider?: boolean;
    leftColor?: string;
    rightColor?: string;
};

export const TaskListItem: React.FC<TaskListItemProps> = ({
    task,
    now,
    onOpen,
    divider = false,
    leftColor = "text.primary",
    rightColor = "text.secondary",
}) => {
    const deadline = getStatus(task.deadline, now);
    const appearance = urgencyAppearance[deadline.key];

    const number = task.documentInfo?.number?.trim();
    const documentTitle = task.documentInfo?.title?.trim();

    const mainText =
        documentTitle ||
        task.taskDescription ||
        "Zadanie";

    const handleOpen = () => onOpen(task);

    return (
        <>
            <ListItem
                disablePadding
                secondaryAction={
                    <Tooltip title="Otwórz">
                        <IconButton
                            edge="end"
                            size="small"
                            aria-label={`Otwórz: ${number || mainText}`}
                            onClick={handleOpen}
                            sx={{ color: rightColor }}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
                sx={{
                    px: 1,
                    alignItems: "flex-start",
                    position: "relative",

                    "&:hover": {
                        bgcolor: "action.hover",
                    },

                    "&::before": {
                        content: '""',
                        position: "absolute",
                        zIndex: 1,
                        left: 6,
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: 1,
                        bgcolor: appearance.barColor,
                        pointerEvents: "none",
                    },
                }}
            >
                <ListItemButton
                    onClick={handleOpen}
                    sx={{
                        py: 1.25,
                        pl: 1.5,
                        pr: 6,
                        display: "grid",

                        gridTemplateColumns: {
                            xs: "48px minmax(0, 1fr)",
                            sm: "56px minmax(0, 1fr) auto",
                        },

                        columnGap: {
                            xs: 2,
                            sm: 2.5,
                        },

                        rowGap: 1,
                        alignItems: "center",
                        minWidth: 0,
                    }}
                >
                    {/* Avatar */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            alignSelf: "center",
                            ml: {
                                xs: 0.5,
                                sm: 0.75,
                            },
                            color: leftColor,
                        }}
                    >
                        <DocumentAvatar
                            type={task.documentInfo?.type ?? "DOCUMENT"}
                            documentKind={task.documentInfo?.kind}
                            size="small"
                        />
                    </Box>

                    {/* Numer i tytuł */}
                    <Box sx={{ minWidth: 0 }}>
                        {number && (
                            <Typography
                                fontWeight={600}
                                sx={{
                                    fontSize: "0.825rem",
                                    lineHeight: 1.4,
                                    display: "block",
                                    mb: 0.25,
                                    overflowWrap: "anywhere",

                                    color: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "#9CC7FA"
                                            : "#315F92",
                                }}
                            >
                                {number}
                            </Typography>
                        )}

                        <Typography
                            fontWeight={600}
                            sx={{
                                fontSize: "0.925rem",
                                lineHeight: 1.5,
                                overflowWrap: "anywhere",

                                color: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "#E6EAF1"
                                        : "#202B3C",
                            }}
                        >
                            {mainText}
                        </Typography>
                    </Box>

                    {/* Pozycjonowanie terminu należy do układu wiersza */}
                    <Box
                        sx={{
                            gridColumn: {
                                xs: 2,
                                sm: 3,
                            },
                            minWidth: 0,
                        }}
                    >
                        <TaskDeadline
                            deadline={deadline}
                            color={rightColor}
                        />
                    </Box>
                </ListItemButton>
            </ListItem>

            {divider && <Divider component="li" />}
        </>
    );
};